import { useState } from "react";

import clsx from "clsx";

import infoTooltipIcon from "@assets/info-atom-icon.svg";
import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { SecondaryButton } from "@components/v2/buttons";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";

import { ClearMetricsConfirmationModal } from "../clear-metrics-confirmation-modal";

export const Header = () => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    metricsData,
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      filterQuery,
      timeGranularity,
      dateRange,
    },
    setTiles,
  } = useMetricsExplorerContext();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    filterQuery,
    dateRange,
  };

  return (
    <div className="flex items-start justify-between">
      <div>
        <h2 className="font-bold text-xl mb-1 flex items-center gap-1">
          Correlation Analysis
          <GenericTooltip
            customIcon={
              <div className="__hide-on-pdf-export">
                <img
                  src={infoTooltipIcon}
                  alt="infoTooltipIcon"
                  style={{ height: "1rem" }}
                />
              </div>
            }
            content={
              <div className="w-[600px]">
                <p className="my-2">
                  The Pearson correlation coefficient, often denoted as "r" is a
                  statistical measure that quantifies the strength and direction
                  of the linear relationship between two variables.
                </p>
                <h4 className="text-sm my-3">Interpretation</h4>
                <ul className="list-disc space-y-2 px-5">
                  <li>
                    <span className="font-bold">r = 1</span>: A perfect positive
                    linear relationship. As one variable increases, the other
                    variable increases proportionally.
                  </li>
                  <li>
                    <span className="font-bold">r = −1</span>: A perfect
                    negative linear relationship. As one variable increases, the
                    other variable decreases proportionally.
                  </li>
                  <li>
                    <span className="font-bold">r = 0</span>: No linear
                    relationship between the variables.
                  </li>
                </ul>
                <h4 className="text-sm my-3">Examples</h4>
                <ul className="list-disc space-y-2 px-5">
                  <li>
                    <div className="font-bold">
                      Very strong relationship between advertising spend and
                      revenue where r = (0.7 to 1.0).
                    </div>{" "}
                    If a company consistently sees a significant increase in
                    sales revenue whenever it increases its advertising budget,
                    this indicates a strong relationship. For example, doubling
                    the advertising spend might consistently lead to a
                    significant boost in sales.
                  </li>
                  <li>
                    <div className="font-bold">
                      Weak relationship between clicks and customer lifetime
                      value where r = (0 to 0.3).
                    </div>
                    If a company’s ads get a lot of clicks but this doesn’t
                    translate into a significant increase in Customer Lifetime
                    Value, the relationship is weak. People may click on the ad
                    out of curiosity, but that doesn’t necessarily mean they
                    will become repeat customers or spend a lot over time.
                  </li>
                </ul>
              </div>
            }
          />
        </h2>
        <p className="text-sm m-0">
          Measure the relationship between two metrics using the Pearson
          correlation coefficient.
        </p>
      </div>
      <div className="__hide-on-pdf-export flex items-center gap-3">
        <SecondaryButton
          disabled={metricsData.filter((e) => e.status === "done").length === 0}
          className="font-bold flex items-center gap-2"
          onClick={() => {
            prettyLogEventPage({
              domain: "Metrics Explorer",
              action: "onClick",
              additionalActionInfo: "Open confirmation modal",
            });

            setIsConfirmationModalOpen(true);
          }}
        >
          <i
            className={clsx("fa-regular fa-trash", {
              "text-mars":
                metricsData.filter((e) => e.status === "done").length > 0,
              "text-gray+1":
                metricsData.filter((e) => e.status === "done").length === 0,
            })}
          ></i>{" "}
          CLEAR ALL
        </SecondaryButton>
      </div>
      <ClearMetricsConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirmation={() => {
          prettyLogEventPage({
            domain: "Metrics Explorer",
            action: "onClick",
            additionalActionInfo: "Clear all tiles",
            data: {
              pageContext,
            },
          });

          setIsConfirmationModalOpen(false);
          setTiles([]);
        }}
      />
    </div>
  );
};
